import { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Upload } from "lucide-react";

const profileSchema = z.object({
  display_name: z.string().trim().min(1, "Display name is required").max(100, "Display name too long"),
  headline: z.string().trim().max(120, "Headline is too long").optional(),
  bio: z.string().trim().max(500, "Bio must be under 500 characters").optional(),
  location: z.string().trim().max(120, "Location is too long").optional(),
});

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditProfileDialog({ open, onOpenChange }: EditProfileDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const initials = useMemo(() => {
    if (!displayName) return "U";

    return displayName
      .split(" ")
      .map((namePart) => namePart[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [displayName]);

  const syncAuthMetadata = useCallback(
    async (metadata: { display_name?: string; avatar_url?: string | null; headline?: string; bio?: string; location?: string }) => {
      if (!user) return;

      const { error } = await supabase.auth.updateUser({
        data: {
          display_name: metadata.display_name,
          avatar_url: metadata.avatar_url,
          headline: metadata.headline?.trim() || "",
          bio: metadata.bio?.trim() || "",
          location: metadata.location?.trim() || "",
        },
      });

      if (error) {
        console.error("Failed to sync auth metadata:", error);
        toast({
          title: "Profile saved with warnings",
          description: "We updated your profile but could not refresh your session metadata.",
          variant: "destructive",
        });
      }
    },
    [toast, user]
  );

  const loadProfile = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      console.error("Error loading profile:", error);
      toast({
        title: "Could not load profile",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (data) {
      setDisplayName(data.display_name);
      setAvatarUrl(data.avatar_url);
      setHeadline(user.user_metadata?.headline || "");
      setBio(user.user_metadata?.bio || "");
      setLocation(user.user_metadata?.location || "");
      return;
    }

    const fallbackName = user.user_metadata?.display_name || user.email?.split("@")[0] || "User";

    const { data: newProfile, error: upsertError } = await supabase
      .from("profiles")
      .upsert(
        {
          user_id: user.id,
          display_name: fallbackName,
          avatar_url: user.user_metadata?.avatar_url || null,
        },
        { onConflict: "user_id" }
      )
      .select("display_name, avatar_url")
      .single();

    if (upsertError) {
      console.error("Error creating missing profile:", upsertError);
      return;
    }

    setDisplayName(newProfile.display_name);
    setAvatarUrl(newProfile.avatar_url);
    setHeadline(user.user_metadata?.headline || "");
    setBio(user.user_metadata?.bio || "");
    setLocation(user.user_metadata?.location || "");
  }, [toast, user]);

  useEffect(() => {
    if (!open) return;
    loadProfile();
  }, [open, loadProfile]);

  const handleAvatarUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Avatar must be less than 2MB.",
        variant: "destructive",
      });
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      const extension = file.name.split(".").pop();
      const fileName = `${user.id}-${Date.now()}.${extension}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage.from("guest-avatars").upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("guest-avatars").getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", user.id);
      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      await syncAuthMetadata({ avatar_url: publicUrl });

      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error uploading avatar:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload avatar.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateProfile = async (event: FormEvent) => {
    event.preventDefault();

    let parsedProfile: z.infer<typeof profileSchema>;

    try {
      parsedProfile = profileSchema.parse({ display_name: displayName, headline, bio, location });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .upsert({ user_id: user!.id, display_name: parsedProfile.display_name, avatar_url: avatarUrl }, { onConflict: "user_id" });

      if (error) throw error;

      await syncAuthMetadata({
        display_name: parsedProfile.display_name,
        avatar_url: avatarUrl,
        headline: parsedProfile.headline,
        bio: parsedProfile.bio,
        location: parsedProfile.location,
      });

      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.warn("Session refresh failed after profile update:", refreshError);
      }

      toast({
        title: "Profile updated",
        description: "Your profile details have been updated successfully.",
      });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>Update your profile information that other attendees will see.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleUpdateProfile} className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={avatarUrl || undefined} alt={displayName} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <Label htmlFor="avatar" className="flex items-center gap-2 cursor-pointer">
                <Button type="button" variant="outline" asChild disabled={uploading}>
                  <span>
                    <Upload className="mr-2 h-4 w-4" />
                    {uploading ? "Uploading..." : "Upload Avatar"}
                  </span>
                </Button>
              </Label>
              <Input
                id="avatar"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarUpload}
                disabled={uploading}
              />
              <p className="text-xs text-muted-foreground">PNG, JPG up to 2MB.</p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Enter your display name"
              maxLength={100}
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="headline">Headline</Label>
              <Input
                id="headline"
                value={headline}
                onChange={(event) => setHeadline(event.target.value)}
                placeholder="Tell people what you do"
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={location}
                onChange={(event) => setLocation(event.target.value)}
                placeholder="City, Country"
                maxLength={120}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={bio}
              onChange={(event) => setBio(event.target.value)}
              placeholder="Share a short description to introduce yourself"
              rows={4}
              maxLength={500}
            />
          </div>

          <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="sm:order-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="sm:order-2">
              {loading ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditProfileDialog;
